import { FC, useEffect, useRef, useState } from 'react';
import { BKJIcon } from '@bookjane2/bookjane-design-library';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { useClickOutside } from 'hooks';
import { mergeRefs } from 'utils/mergeRefs';
import { getJ360ElementNode } from 'utils/getJ360ElementNode';
import { DROPDOWN_ROOT_ID } from 'constants/elements';
import {
  BKJDatePickerContext,
  CalendarPopUp,
  IBKJDatePickerChangeEvent,
} from 'components/BKJDatePicker';
import { getPrettyDayAndDateFromDate } from 'components/BKJDatePicker/src/BKJDatePicker.utils';
import { DateRangeTrigger } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/FilterBar.styled';

interface ReportsDateInputProps {
  name: string;
  value: string;
  onChange: (isoDate: string) => void;
  disabledFrom?: string;
  disabledTo?: string;
  ariaLabel?: string;
  calendarOffset?: [number, number];
}

export const ReportsDateInput: FC<ReportsDateInputProps> = ({
  name,
  value,
  onChange,
  disabledFrom,
  disabledTo,
  ariaLabel,
  calendarOffset = [12, 6],
}) => {
  const popupRef = useRef<HTMLElement | null>(null);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(
    getJ360ElementNode(DROPDOWN_ROOT_ID),
  );
  const [isOpen, setIsOpen] = useState(false);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [{ name: 'offset', options: { offset: calendarOffset } }],
    placement: 'bottom-start',
  });

  useEffect(() => {
    if (!targetElement) {
      const element = document.createElement('div');
      element.setAttribute('id', DROPDOWN_ROOT_ID);
      document.body.appendChild(element);
      setTargetElement(element);
    }
  }, [targetElement]);

  useClickOutside([popupRef.current, targetElement, referenceElement], () => setIsOpen(false));

  const handleChange = (event: IBKJDatePickerChangeEvent): void => {
    const rawValue = event.target.value;
    const picked = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    if (typeof picked === 'string' && picked) onChange(picked);
    setIsOpen(false);
  };

  return (
    <BKJDatePickerContext.Provider
      value={{
        type: 'SingleDate',
        variant: 'SSFTopBar',
        name,
        value,
        onChange: handleChange,
        disabledFrom,
        disabledTo,
      }}
    >
      <DateRangeTrigger
        type="button"
        $isOpen={isOpen}
        aria-label={ariaLabel}
        ref={setReferenceElement}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {getPrettyDayAndDateFromDate(value)}
        <BKJIcon iconName="ShiftsDate" fontSize={14} color="TextDarkGrey" />
      </DateRangeTrigger>
      {isOpen &&
        !!targetElement &&
        createPortal(
          <CalendarPopUp
            isCalendarOpen={isOpen}
            setIsCalendarOpen={setIsOpen}
            context={BKJDatePickerContext}
            ref={mergeRefs([popupRef, setPopperElement])}
            style={styles.popper}
            {...attributes.popper}
          />,
          targetElement,
        )}
    </BKJDatePickerContext.Provider>
  );
};
